'use-client';

import type { ClassNames } from '../HamburgerCloseButton.types';
import type { RefObject } from 'react';
import React, { useEffect, useState } from 'react';
import type {
  IStylableButtonImperativeActions,
  IStylableButtonProps,
} from '../../../StylableButton/StylableButton.types';
import { useHamburgerMenuContext } from '../../HamburgerMenuContext';
import semanticClassNames from '../HamburgerCloseButton.semanticClassNames';
import classNames from 'classnames';

const getMenuTransitionChildren = (
  menuContainerRef: RefObject<HTMLDivElement> | undefined,
) => {
  if (menuContainerRef?.current) {
    const children = Array.from(
      menuContainerRef?.current?.querySelectorAll('*'),
    );

    return children.filter(child => {
      const { transitionProperty, transitionDuration, transitionDelay } =
        window.getComputedStyle(child);

      return (
        (transitionProperty === 'all' || transitionProperty === 'visibility') &&
        (transitionDelay !== '0s' || transitionDuration !== '0s')
      );
    }) as Array<HTMLElement>;
  }

  return [];
};

const HamburgerCloseButton: React.ForwardRefRenderFunction<
  IStylableButtonImperativeActions,
  IStylableButtonProps & {
    classes: ClassNames;
    StylableButton: React.ComponentType<IStylableButtonProps>;
  }
> = (
  { id, a11y = {}, className, classes, StylableButton, onClick, ...props },
  ref,
) => {
  const { isMenuOpen, setIsMenuOpen, menuContainerRef } =
    useHamburgerMenuContext();

  const [menuTransitionChildren, setMenuTransitionChildren] = useState<
    Array<HTMLElement>
  >([]);

  useEffect(() => {
    if (!isMenuOpen) {
      menuTransitionChildren.forEach(child => {
        child.style.display = '';
      });
    }
  }, [isMenuOpen, menuTransitionChildren]);

  const onButtonClick = (event: React.MouseEvent) => {
    const children = getMenuTransitionChildren(menuContainerRef);
    setMenuTransitionChildren(children);

    children.forEach(child => {
      child.style.display = 'none';
    });

    setIsMenuOpen(false);
    onClick?.(event);
  };

  return (
    <StylableButton
      {...props}
      id={id}
      className={classNames(
        classes.hamburgerCloseButtonRoot,
        classes.stylableButtonRoot,
        className,
      )}
      ref={ref}
      a11y={a11y}
      onClick={onButtonClick}
      semanticClassNames={semanticClassNames}
    />
  );
};

export default React.forwardRef(HamburgerCloseButton);
